import React, { useState, useEffect, useRef } from "react";
import { Box, TextField, Button, Typography, Container } from "@mui/material";
import logoImage from "../../assets/images/logo.png";
import callApi from "../../services/callApi";
import debounce from "lodash/debounce";
import ReferralBanner from "./components/ReferralBanner";

// ─── Estilos compartidos ─────────────────────────────────────────────────────

const cardSx = {
  width: "100%",
  p: { xs: "20px", md: "28px" },
  borderRadius: { xs: "16px", md: "20px" },
  background: "#ffffff",
  boxShadow: "0 2px 4px rgba(0,0,0,0.03), 0 8px 16px rgba(0,0,0,0.03), 0 12px 24px rgba(0,0,0,0.05)",
  border: "1px solid #eaeaea",
  boxSizing: "border-box",
};

const btnSx = {
  mt: 2,
  height: { xs: "46px", md: "48px" },
  borderRadius: "10px",
  bgcolor: "#1877f2",
  fontFamily: "Quicksand-Bold",
  fontSize: { xs: "15px", md: "16px" },
  textTransform: "none",
  boxShadow: "0 4px 12px rgba(24, 119, 242, 0.28)",
  transition: "all 0.2s ease",
  "&:hover": { bgcolor: "#166fe5", boxShadow: "0 6px 16px rgba(24, 119, 242, 0.38)" },
  "&.Mui-disabled": { bgcolor: "rgba(24, 119, 242, 0.35)", color: "white", boxShadow: "none" },
};

// ─── Paso 3: Perfil Web ───────────────────────────────────────────────────────

const WebProfileInfo = ({ userData, onNext, onBack }) => {
  const [profileUrl, setProfileUrl] = useState("");
  const [baseUrl]   = useState("clinifica.com/pro/");
  const [isLoading, setIsLoading]   = useState(false);
  const [isAvailable, setIsAvailable] = useState(null);
  const [isFormValid, setIsFormValid] = useState(false);

  const currentUrlRef      = useRef(profileUrl);
  const abortControllerRef = useRef(null);

  const normalizeText = (text) =>
    text.toLowerCase().normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");

  useEffect(() => {
    if (userData?.nombres || userData?.apellidos) {
      const suggested = normalizeText(`${userData.nombres || ""} ${userData.apellidos || ""}`);
      setProfileUrl(suggested);
      if (suggested.length >= 5) checkUrlAvailability(suggested);
    }
  }, [userData]);

  useEffect(() => { currentUrlRef.current = profileUrl; }, [profileUrl]);

  useEffect(() => {
    setIsFormValid(/^[a-z0-9-]+$/.test(profileUrl) && profileUrl.length >= 5 && profileUrl.length <= 50);
  }, [profileUrl]);

  const checkUrlAvailability = debounce(async (urlToCheck) => {
    if (urlToCheck !== currentUrlRef.current) return;
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    if (urlToCheck.length < 5) { setIsAvailable(false); return; }
    setIsAvailable(null);
    setIsLoading(true);
    try {
      const response = await callApi(`/registro/validarLink?link_plataforma=${urlToCheck}`, "get", null, { signal: abortControllerRef.current.signal });
      setIsLoading(false);
      if (urlToCheck === currentUrlRef.current) setIsAvailable(response?.disponible === "disponible");
    } catch (error) {
      if (error.name === "AbortError") return;
      if (urlToCheck === currentUrlRef.current) setIsAvailable(false);
      setIsLoading(false);
    }
  }, 500);

  const handleInputChange = (e) => {
    const value = e.target.value.trim().toLowerCase();
    setProfileUrl(value);
    if (value.length >= 5) checkUrlAvailability(value);
    else setIsAvailable(false);
  };

  useEffect(() => () => { if (abortControllerRef.current) abortControllerRef.current.abort(); }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isAvailable) return;
    onNext({ ...userData, profileUrl, fullProfileUrl: baseUrl + profileUrl });
  };

  return (
    <Box sx={{
      minHeight: "100vh",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#ffffff",
      py: { xs: 3, md: 4 },
    }}>
      <Container maxWidth="xs" sx={{ px: { xs: 2, md: 2 } }}>
        <Box sx={cardSx}>
          <Box sx={{ mb: { xs: 2, md: 2.5 }, display: "flex", justifyContent: "center" }}>
            <img src={logoImage} alt="Clinifica" style={{ height: "34px" }} />
          </Box>

          <Typography sx={{ fontFamily: "Poppins-SemiBold", fontSize: { xs: "18px", md: "20px" }, color: "#1c1c1c", mb: "6px" }}>
            Hola {userData?.nombres} 👋
          </Typography>
          <Typography sx={{ fontFamily: "Quicksand-Regular", fontSize: { xs: "13px", md: "14px" }, color: "#6b7280", mb: { xs: "20px", md: "24px" }, lineHeight: 1.5 }}>
            Elige la dirección de tu perfil web. Tus pacientes agendarán citas contigo desde ese link.
          </Typography>

          <form onSubmit={handleSubmit}>
            <Typography sx={{ mb: "6px", fontSize: { xs: "13px", md: "14px" }, fontFamily: "Quicksand-Bold", color: "#1c1c1c" }}>
              Perfil web
            </Typography>

            {/* Campo URL inline */}
            <Box sx={{
              display: "flex", alignItems: "center",
              border: "1px solid #e4e8ef",
              borderRadius: "10px",
              backgroundColor: "#f7f9fc",
              overflow: "hidden",
              px: { xs: "10px", md: "12px" },
              height: { xs: "44px", md: "46px" },
              mb: "8px",
              "&:focus-within": { borderColor: "#1877f2", borderWidth: "1.5px" },
            }}>
              <Typography sx={{ fontFamily: "Quicksand-Regular", fontSize: { xs: "11px", md: "12px" }, color: "#9ca3af", whiteSpace: "nowrap", flexShrink: 0 }}>
                {baseUrl}
              </Typography>
              <TextField
                fullWidth variant="standard" placeholder="nombre-apellido"
                value={profileUrl} onChange={handleInputChange}
                InputProps={{ disableUnderline: true }}
                sx={{
                  flex: 1,
                  "& .MuiInput-root": {
                    fontFamily: "Quicksand-Regular",
                    fontSize: { xs: "13px", md: "14px" },
                    "&:before, &:after": { display: "none" },
                  },
                  "& .MuiInputBase-input": { padding: "0 4px" },
                }}
              />
            </Box>

            {/* Estado disponibilidad */}
            {isAvailable === true && (
              <Typography sx={{ mb: "8px", textAlign: "center", fontFamily: "Quicksand-Bold", fontSize: { xs: "13px", md: "14px" }, color: "#16a34a" }}>
                ✓ Perfil web disponible
              </Typography>
            )}
            {isAvailable === false && profileUrl.length >= 5 && (
              <Typography sx={{ mb: "8px", fontFamily: "Quicksand-Bold", fontSize: { xs: "13px", md: "14px" }, color: "#d32f2f", textAlign: "center" }}>
                ✗ Perfil no disponible
              </Typography>
            )}

            <Typography sx={{ fontFamily: "Quicksand-Regular", fontSize: { xs: "11px", md: "12px" }, color: "#9ca3af", mb: { xs: "16px", md: "20px" }, lineHeight: 1.5 }}>
              Solo letras minúsculas y guiones — mínimo 5 caracteres. Ej: dr-nombre-apellido
            </Typography>

            <Button type="submit" fullWidth variant="contained" disabled={!isFormValid || isAvailable === false || isLoading} sx={btnSx}>
              Continuar
            </Button>
          </form>

          <Button variant="text" onClick={onBack} sx={{ color: "#6b7280", textTransform: "none", fontFamily: "Quicksand-Regular", fontSize: { xs: "13px", md: "14px" }, mt: 1, p: 0 }}>
            volver
          </Button>
        </Box>

        <Box sx={{ mt: "12px" }}>
          <ReferralBanner />
        </Box>
      </Container>
    </Box>
  );
};

export default WebProfileInfo;
